import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React, { useCallback, useEffect, useRef } from 'react';
import { SearchAnchor, EnlargedSearchAnchor, KeyboardHighlightProvider } from '@atlassian/search-dialog';
import { useDialogExpansionContext } from '../dialog-expansion-context';
import { mergeRefCallback } from '../../utils/merge-ref-callback';
import { useFeaturesContext } from '../features-context/features-context';
export class KeyboardWrapperStateless extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      ref: null
    });
    /**
     * Hold a timeout id for the onBlur and onFocus handlers
     */
    _defineProperty(this, "timeout", undefined);
    _defineProperty(this, "handleKeyDown", event => {
      if (event.key === 'Escape' && !event.defaultPrevented && this.props.isExpanded) {
        event.preventDefault();
        this.close();
      }
    });
    _defineProperty(this, "onBlurHandler", () => {
      this.timeout = window.setTimeout(() => {
        this.close();
      });
    });
    _defineProperty(this, "onFocusHandler", () => {
      window.clearTimeout(this.timeout);
    });
    _defineProperty(this, "setRef", ref => {
      if (this.state.ref !== ref) {
        this.setState({
          ref
        });
      }
    });
    _defineProperty(this, "close", () => {
      const {
        ref
      } = this.state;
      ref && ref.blur();
      this.props.onClose();
    });
  }
  render() {
    const {
      isExpanded,
      isQuickFind,
      children
    } = this.props;
    const SearchAnchorComponent = isQuickFind ? EnlargedSearchAnchor : SearchAnchor;
    return /*#__PURE__*/React.createElement(SearchAnchorComponent, {
      onBlur: this.onBlurHandler,
      onFocus: this.onFocusHandler,
      onKeyDown: this.handleKeyDown,
      isExpanded: isExpanded
    }, /*#__PURE__*/React.createElement(KeyboardHighlightProvider, {
      listenerNode: this.state.ref
    }, children ? children(this.setRef) : null));
  }
}
export const KeyboardWrapper = ({
  children,
  keepExpandedInTab = false,
  forwardRef,
  ...rest
}) => {
  var _useFeaturesContext$q;
  const {
    isExpanded,
    setIsExpanded,
    allowChangeExpand
  } = useDialogExpansionContext();
  const inputRef = useRef(null);
  const mergedForwardref = mergeRefCallback(inputRef, forwardRef);
  const isQuickFind = (_useFeaturesContext$q = useFeaturesContext().quickFind) === null || _useFeaturesContext$q === void 0 ? void 0 : _useFeaturesContext$q.enabled;
  const onClose = useCallback(() => {
    setIsExpanded(false);
  }, [setIsExpanded]);
  const onTabChange = useCallback(() => {
    if (document.hidden) {
      allowChangeExpand(false);
    } else {
      if (isExpanded) {
        var _inputRef$current, _inputRef$current$foc;
        inputRef === null || inputRef === void 0 ? void 0 : (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : (_inputRef$current$foc = _inputRef$current.focus) === null || _inputRef$current$foc === void 0 ? void 0 : _inputRef$current$foc.call(_inputRef$current);
      }
      allowChangeExpand(true);
    }
  }, [allowChangeExpand, isExpanded]);
  useEffect(() => {
    if (keepExpandedInTab) {
      document.addEventListener('visibilitychange', onTabChange);
      return () => {
        document.removeEventListener('visibilitychange', onTabChange);
      };
    }
  }, [onTabChange, keepExpandedInTab]);
  return /*#__PURE__*/React.createElement(KeyboardWrapperStateless, _extends({}, rest, {
    onClose: onClose,
    isExpanded: isExpanded,
    isQuickFind: isQuickFind
  }), setRef => {
    const ref = mergeRefCallback(setRef, mergedForwardref);
    const onRetry = () => {
      var _inputRef$current2, _inputRef$current2$fo;
      inputRef === null || inputRef === void 0 ? void 0 : (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 ? void 0 : (_inputRef$current2$fo = _inputRef$current2.focus) === null || _inputRef$current2$fo === void 0 ? void 0 : _inputRef$current2$fo.call(_inputRef$current2);
    };
    return children({
      ref,
      onRetry
    });
  });
};